import { toLegalEntity } from '@/model/LegalEntity';
import type { AppDispatch } from '@/store/store';
import { setSelectedEntity, setUserEntities, setUserPermissions } from '@/store/user/userSlice';
import { isDefined, isNotDefined } from '@/utils/assertions';

type AvailablePermissions =
  | 'CanSeeDebitAccountsOnHomepage'
  | 'CanSeeMyDesk'
  | 'CanSeeMyPortfolio'
  | 'CanSeeOSTTabOnHomepage'
  | 'CanSeePortfolioTabOnHomepage'
  | 'CanUseCashTransfer'
  | 'canSeeRestrictedData'
  | 'SGM_COCKPIT'
  | 'canSeeAsmLink'
  | 'CanSeeLuxData'
  | 'CanSeeMcoData'
  | 'CashTransferAudit'
  | 'CanDoArrayExport'
  | 'CanSeeFutureLiquidities'
  | 'CanSeeBookOrder'
  | 'CanSeeAlertTabOnRoot'
  | 'CanSeePositionDetails'
  | 'isBranch'
  ;

type Authorization = {
  resource: string;
  permissions: {
    name: AvailablePermissions;
    constraints: Array<{ values: string[], name: string }>;
  }[];
};

export type UserAuthorization = {
  user_authorization: Authorization[];
};

export const hasSgpbCockpitIAMPermissions = (
  dispatch: AppDispatch,
  userInfo?: UserAuthorization,
) => {
  if (isNotDefined(userInfo)) {
    return false;
  }

  const hasRightAccess =
    userInfo?.user_authorization.some((authorization) =>
      authorization.permissions.some((permission) => permission.name === 'SGM_COCKPIT'),
    ) ?? false;

  if (!hasRightAccess) {
    redirectToRequestAccess();
  }

  const cockpitAuthorization = userInfo?.user_authorization.find(
    (userAuthorization) => userAuthorization.resource === 'api.cockpit-edge-service',
  )?.permissions;

  if (cockpitAuthorization) {
    const hasAuth: (authName: string) => boolean = (authName: string) =>
      isDefined(cockpitAuthorization.find(({ name }) => name === authName));

    const canSeeMyDesk: boolean = hasAuth('CanSeeMyDesk');
    const canSeeMyPortfolio: boolean = hasAuth('CanSeeMyPortfolio');

    const canSeePortfolioTabOnHomepage: boolean = hasAuth('CanSeePortfolioTabOnHomepage');
    const canSeeOstTabOnHomepage: boolean = hasAuth('CanSeeOstTabOnHomepage');
    const canSeeCashTransfersOnHomepage: boolean = hasAuth('CanSeeCashTransfersOnHomepage');
    const canSeeCashSearchOnHomepage: boolean = hasAuth('CanSeeCashSearchOnHomepage')
    const canSeeDebitAccountsOnHomepage: boolean = hasAuth('CanSeeDebitAccountsOnHomepage');

    const canSearchRoots: boolean = hasAuth('CanSearchRoots');
    const canSearchPersons: boolean = hasAuth('CanSearchPersons');
    const canSeeApplicationsLinks: boolean = hasAuth('CanSeeApplicationsLinks');

    const canSeeCashTransfers: boolean = hasAuth('CanSeeCashTransfers');
    const canDoCashTransfers: boolean = hasAuth('CanDoCashTransfers');
    const canDoMaksOperations: boolean = hasAuth('CanDoMaksOperations');
    const canDoDocumentGeneration: boolean = hasAuth('CanDoDocumentGeneration');
    const canSeeRestrictedData: boolean = hasAuth('canSeeRestrictedData');
    const canSeeAsmLink: boolean = hasAuth('canSeeAsmLink');
    const cashTransferAudit: boolean = hasAuth('CashTransferAudit');
    const arrayExport: boolean = hasAuth('CanDoArrayExport')
    const bookOrder: boolean = hasAuth('CanSeeBookOrder')
    const futureLiquidities: boolean = hasAuth('CanSeeFutureLiquidities')
    const seeAlertTabOnRoot: boolean = hasAuth('CanSeeAlertTabOnRoot')
    const isBranchMember: boolean = hasAuth('isBranch')
    const seePositionDetails: boolean = hasAuth('CanSeePositionDetails')
    const seeSigmaOperations: boolean = hasAuth('CanSeeSigmaOperations')
    const seeInputDocs : boolean = hasAuth('CanSeeInputDocuments')
    const seeOutputDocs : boolean = hasAuth('CanSeeOutputDocuments')
    const seeFeesTab: boolean = hasAuth('CanSeeFeesTab')

    dispatch(
      setUserPermissions({
        canAccess: true,

        canSeeMyDesk: canSeeMyDesk,
        canSeeMyPortfolio: canSeeMyPortfolio,

        canSeePortfolioTabOnHomepage: canSeePortfolioTabOnHomepage,
        canSeeOstTabOnHomepage: canSeeOstTabOnHomepage,
        canSeeCashTransfersOnHomepage: canSeeCashTransfersOnHomepage,
        canSeeCashSearchOnHomepage : canSeeCashSearchOnHomepage,
        canSeeDebitAccountsOnHomepage: canSeeDebitAccountsOnHomepage,

        canSearchRoots: canSearchRoots,
        canSearchPersons: canSearchPersons,
        canSeeApplicationsLinks: canSeeApplicationsLinks,

        canSeeRestrictedData: canSeeRestrictedData,

        canSeeCashTransfers: canSeeCashTransfers,
        canDoCashTransfers: canDoCashTransfers,
        canDoMaksOperations: canDoMaksOperations,
        canDoDocumentGeneration: canDoDocumentGeneration,
        canSeeAsmLink: canSeeAsmLink,
        cashTransferAudit: cashTransferAudit,
        canDoArrayExport: arrayExport,
        canSeeFutureLiquidities: futureLiquidities,
        canSeeBookOrder: bookOrder,
        canSeeAlertTabOnRoot: seeAlertTabOnRoot,
        canSeePositionDetails: seePositionDetails,
        isBranch: isBranchMember,
        canSeeSigmaOperations: seeSigmaOperations,
        canSeeInputDocuments: seeInputDocs,
        canSeeOutputDocuments: seeOutputDocs,
        canSeeFeesTab: seeFeesTab,
      }),
    );

    const fimWithLuxData = !cockpitAuthorization.some(p => p.name === 'canSeeRestrictedData')
                        && cockpitAuthorization.some(p => p.name === 'CanSeeLuxData')
    const fimWithMcoData = !cockpitAuthorization.some(p => p.name === 'canSeeRestrictedData')
                        && cockpitAuthorization.some(p => p.name === 'CanSeeMcoData')
    if(fimWithLuxData && fimWithMcoData) {
      dispatch(setUserEntities(['LUX', 'MCO']));
      dispatch(setSelectedEntity('LUX'))
    } else if(fimWithLuxData) {
      dispatch(setUserEntities(['LUX']));
      dispatch(setSelectedEntity('LUX'))
    } else if(fimWithMcoData) {
      dispatch(setUserEntities(['MCO']));
      dispatch(setSelectedEntity('MCO'))
    } else { // internal user
      const entities = cockpitAuthorization.flatMap(p => p.constraints).find(c => c.name === 'COCKPIT_LEGAL_ENTITY')
                        ?.values?.map(v => toLegalEntity(v)) ?? []
      dispatch(setUserEntities(entities));
      dispatch(setSelectedEntity(entities[0]))
    }
  }
};

export const redirectToRequestAccess = () => {
  window.location.replace(window?.sgmeConfiguration?.access_request_url + location.href);
};
